import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { listWorkforceClaims } from "../../api/workforce";
import { listPreCredentialApplications } from "../../api/precredential";
import { isDeletedDoctor } from "../../utils/deletedDoctor";
import { isPreCredentialPendingStatus } from "../../utils/hireInboxUtils";

export type BankCounts = {
  internalRequests: number;
  preCredentialRequests: number;
  totalRequests: number;
};

const EMPTY: BankCounts = {
  internalRequests: 0,
  preCredentialRequests: 0,
  totalRequests: 0,
};

const BANK_COUNTS_EVENT = "bank-counts-changed";

let cachedHospitalId = "";
let cachedCounts: BankCounts = EMPTY;
let cachedAt = 0;
const CACHE_MS = 20_000;

async function loadBankCounts(
  hospitalId: string,
  force = false,
): Promise<BankCounts> {
  if (
    !force &&
    cachedHospitalId === hospitalId &&
    Date.now() - cachedAt < CACHE_MS
  ) {
    return cachedCounts;
  }
  const [claims, applications] = await Promise.all([
    listWorkforceClaims(),
    listPreCredentialApplications(String(hospitalId)).catch(() => []),
  ]);
  const internalRequests = Array.isArray(claims) ? claims.length : 0;
  const preCredentialRequests = (Array.isArray(applications) ? applications : []).filter(
    (row) => !isDeletedDoctor(row) && isPreCredentialPendingStatus(row.status),
  ).length;
  cachedCounts = {
    internalRequests,
    preCredentialRequests,
    totalRequests: internalRequests + preCredentialRequests,
  };
  cachedHospitalId = hospitalId;
  cachedAt = Date.now();
  return cachedCounts;
}

export function invalidateBankCounts() {
  cachedAt = 0;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(BANK_COUNTS_EVENT));
  }
}

export function useBankCounts() {
  const hospitalId = useSelector(
    (state: any) => state.currentHospital?.current?.id,
  );
  const [counts, setCounts] = useState<BankCounts>(
    hospitalId && cachedHospitalId === String(hospitalId) ? cachedCounts : EMPTY,
  );

  useEffect(() => {
    let cancelled = false;
    if (!hospitalId) {
      setCounts(EMPTY);
      return;
    }

    const apply = (next: BankCounts) => {
      if (!cancelled) setCounts(next);
    };

    void loadBankCounts(String(hospitalId)).then(apply);

    const refresh = () => {
      void loadBankCounts(String(hospitalId), true).then(apply);
    };

    window.addEventListener(BANK_COUNTS_EVENT, refresh);
    window.addEventListener("workforce-changed", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener(BANK_COUNTS_EVENT, refresh);
      window.removeEventListener("workforce-changed", refresh);
    };
  }, [hospitalId]);

  return counts;
}
